/**
 * Context that provides mode-aware intake data to all intake flow steps.
 *
 * Wrapping the flow in this context lets StepProviders, StepLocation,
 * StepBooking, and StepConfirmation read real or mock data without
 * each step needing its own mode logic.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { UseIntakeDataReturn } from './useIntakeData';
import { useIntakeData } from './useIntakeData';
import { useIntakeClientAuth } from './useEnsureIntakeClientCredentials';

const IntakeDataCtx = createContext<UseIntakeDataReturn | null>(null);

export function IntakeDataProvider({
  children,
  prefetchProviders = false,
  intakeLinkToken,
  intakeOrganizationId,
  practiceOrganizationId,
  staffAddClient = false,
}: {
  children: ReactNode;
  /** Load referral providers on mount (directory). Public intake loads before step 10 instead. */
  prefetchProviders?: boolean;
  /** Practice-specific intake link token. When present, scopes data to that organization. */
  intakeLinkToken?: string;
  /** Organization id from practice intake URL (?org=). Skips client Organization search when set. */
  intakeOrganizationId?: string;
  /** Staff add-client: load practitioners from this practice for manual provider selection. */
  practiceOrganizationId?: string;
  /** Signed-in Practitioner add-client route — never swap to public OAuth client_credentials. */
  staffAddClient?: boolean;
}): JSX.Element {
  const { ready, authError } = useIntakeClientAuth({ staffAddClient });
  const data = useIntakeData({
    enabled: ready,
    authError,
    prefetchProviders,
    intakeLinkToken,
    intakeOrganizationId,
    practiceOrganizationId,
  });
  return <IntakeDataCtx.Provider value={data}>{children}</IntakeDataCtx.Provider>;
}

export function useIntakeDataContext(): UseIntakeDataReturn {
  const ctx = useContext(IntakeDataCtx);
  if (!ctx) {
    throw new Error('useIntakeDataContext must be used within IntakeDataProvider');
  }
  return ctx;
}
